// src/components/DynamicQuestionGroupComponent.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormHelperText,
  FormGroup,
} from "@mui/material";
import DOMPurify from "dompurify";

const DynamicQuestionGroupComponent = ({
  module,
  responses,
  handleChange,
  isEditable,
  validationErrors,
  isPreview = false,
}) => {
  const { questions = [], randomise = 0 } = module;

  const [selectedQuestions, setSelectedQuestions] = useState([]);

  // --- helpers ---
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const arraysEqual = (a = [], b = []) =>
    a.length === b.length && a.every((v, i) => v === b[i]);

  // The ids persisted by the parent (if any)
  const persistedIds =
    responses?.[module.id] && responses[module.id].__shownIds
      ? responses[module.id].__shownIds
      : null;

  // Decide which questions this instance should render
  const computedSubset = useMemo(() => {
    if (Array.isArray(persistedIds) && persistedIds.length) {
      const idSet = new Set(persistedIds);
      return questions.filter((q) => idSet.has(q.id));
    }
    if (randomise > 0 && questions.length > 0) {
      return shuffleArray(questions).slice(0, randomise);
    }
    return questions;
    // NOTE: don't depend on responses here; we only care about persistedIds snapshot
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, randomise /* persistedIds included via outer variable */]);

  // On mount / when questions list changes meaningfully: set local state
  useEffect(() => {
    setSelectedQuestions(computedSubset);
  }, [computedSubset]);

  // Persist the subset ids to the parent if not already persisted or out of sync
  useEffect(() => {
    if (isPreview) return; // don't persist from previews
    const ids = computedSubset.map((q) => q.id);
    if (!Array.isArray(persistedIds) || !arraysEqual(persistedIds, ids)) {
      handleChange?.(`${module.id}.__shownIds`, ids);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedSubset, isPreview, module.id]);

  if (!Array.isArray(selectedQuestions)) return null;

  const handleAnswerChange = (questionId, value) => {
    if (!isPreview) handleChange(questionId, value);
  };

  return (
    <Box sx={{ marginBottom: "20px" }}>
      {selectedQuestions.map((question, index) => (
        <Box key={question.id} sx={{ marginBottom: "5px" }}>
          <Typography variant="body1">
            {`${index + 1}. ${DOMPurify.sanitize(question.label)}`}
          </Typography>

          {question.type === "short_answer" && (
            <TextField
              value={responses[question.id] || ""}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              fullWidth
              margin="dense"
              disabled={!isEditable && !isPreview}
              error={validationErrors.includes(question.id)}
              helperText={
                validationErrors.includes(question.id)
                  ? "This field is required."
                  : isPreview
                    ? "Sample answer"
                    : ""
              }
              placeholder={isPreview ? "Sample answer" : ""}
            />
          )}

          {(question.type === "checkbox_group" ||
            question.type === "custom_checkbox_group") && (
            <FormControl
              component="fieldset"
              margin="dense"
              error={validationErrors.includes(question.id)}
            >
              <FormGroup>
                {Array.isArray(question.options) &&
                question.options.length > 0 ? (
                  question.options.map((option, idx) => (
                    <FormControlLabel
                      key={idx}
                      control={
                        <Checkbox
                          checked={
                            isPreview
                              ? [question.options[0]].includes(option)
                              : responses[question.id]?.includes(option) ||
                                false
                          }
                          onChange={(e) => {
                            let newValue = responses[question.id] || [];
                            if (e.target.checked)
                              newValue = [...newValue, option];
                            else
                              newValue = newValue.filter((v) => v !== option);
                            handleAnswerChange(question.id, newValue);
                          }}
                          disabled={!isEditable && !isPreview}
                        />
                      }
                      label={option}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="error">
                    No options available.
                  </Typography>
                )}
              </FormGroup>
              {validationErrors.includes(question.id) && (
                <FormHelperText>
                  Please select at least one option.
                </FormHelperText>
              )}
              {isPreview && !validationErrors.includes(question.id) && (
                <FormHelperText>
                  Sample selection: {question.options?.[0]}
                </FormHelperText>
              )}
            </FormControl>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default DynamicQuestionGroupComponent;
